import type { WaveStrip } from '../../hooks/usePipelineSnapshot';

// Section 5 of the spec: full-width aggregate strip showing where the wave is
// across every stage simultaneously. Counts are remaining-work (codex review
// point 1: not raw product_count).

interface Props {
  strip: WaveStrip;
}

interface Col {
  key: string;
  label: string;
  descriptor: string;
  value: number;
}

export function WaveStripBar({ strip }: Props) {
  const cols: Col[] = [
    { key: 'entry', label: 'Entry', descriptor: 'queued', value: strip.entry },
    { key: 'enrich', label: 'Enrich', descriptor: 'in flight', value: strip.enrich },
    { key: 'scoring', label: 'Scoring', descriptor: 'in flight', value: strip.scoring },
    { key: 'embed', label: 'Embed', descriptor: 'in flight', value: strip.embed },
    { key: 'aeoGen', label: 'AEO Gen', descriptor: 'in flight', value: strip.aeoGen },
    { key: 'validate', label: 'Validate', descriptor: 'in flight', value: strip.validate },
    { key: 'trans', label: 'Trans', descriptor: 'in flight', value: strip.trans },
    { key: 'done', label: 'Done', descriptor: strip.doneLabel || 'today', value: strip.done },
  ];

  // Bottleneck signal: amber tint when col > 2× median across in-flight stages
  // (exclude entry + done — different units).
  const inflight = cols.slice(1, 7).map(c => c.value).sort((a, b) => a - b);
  const median = inflight.length ? inflight[Math.floor(inflight.length / 2)] : 0;
  const bottleneckThreshold = median > 0 ? median * 2 : Infinity;

  return (
    <div className="w-full rounded-xl border bg-white shadow-sm overflow-x-auto">
      <div className="flex items-center min-w-max divide-x">
        {cols.map((col, idx) => {
          const isInflight = idx > 0 && idx < cols.length - 1;
          const bottleneck = isInflight && col.value > bottleneckThreshold;
          return (
            <div key={col.key} className="flex items-center">
              <div
                className={`flex flex-col items-center justify-center px-6 py-4 min-w-[110px] transition-colors duration-300 ${bottleneck ? 'bg-amber-50' : ''}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
                  {col.label}
                </div>
                <div className="mt-1 text-2xl font-bold font-mono tabular-nums">
                  {col.value.toLocaleString()}
                </div>
                <div className="text-[10px] text-gray-400 leading-tight">{col.descriptor}</div>
              </div>
              {idx < cols.length - 1 && (
                <div className="px-1 text-gray-300 self-center select-none">→</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
